import { useEffect, useRef } from 'react';
import { howItWorksSteps } from '@/mocks/homeData';

export default function HowItWorksSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.step-animate').forEach((child, i) => {
              (child as HTMLElement).style.animationDelay = `${i * 0.2}s`;
              child.classList.add('animate-fade-in-up');
            });
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} id="how-it-works" className="relative w-full py-24 md:py-32 bg-background-950">
      {/* Yellow accent line top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/30 to-transparent" />

      <div className="w-full px-6 md:px-10 lg:px-16">
        {/* Section header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <p className="step-animate opacity-0 text-primary-500 text-xs font-bold tracking-[0.3em] uppercase mb-4">
            How It Works
          </p>
          <h2 className="step-animate opacity-0 text-3xl sm:text-4xl md:text-5xl font-black text-foreground-50 tracking-tight leading-tight">
            Three steps to<br />
            <span className="text-primary-500">the perfect night.</span>
          </h2>
        </div>

        {/* Steps — horizontal on desktop, stacked on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-10">
          {howItWorksSteps.map((s, i) => (
            <div key={s.step} className="step-animate opacity-0 relative group">
              {/* Connector line (desktop only) */}
              {i < 2 && (
                <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-px bg-gradient-to-r from-primary-500/40 to-transparent" />
              )}

              <div className="flex flex-col">
                {/* Step badge */}
                <div className="flex items-center gap-4 mb-6">
                  <span className="w-12 h-12 rounded-xl bg-primary-500 text-background-950 font-black text-lg flex items-center justify-center shrink-0">
                    {s.step}
                  </span>
                  <h3 className="text-xl font-bold text-foreground-50">{s.title}</h3>
                </div>

                {/* Phone mockup */}
                <div className="phone-frame-dark w-[240px] mx-auto mb-6">
                  <div className="phone-notch-dark" />
                  <img
                    src={s.screenshot}
                    alt={s.title}
                    className="w-full h-auto"
                  />
                </div>

                {/* Description */}
                <p className="text-foreground-500 text-sm leading-relaxed text-center max-w-xs mx-auto">
                  {s.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}